import { GlColumnDef, GlSheetRowVO, buildColumnKey } from '@/services/accountService/glInvoiceService';

// ======================== 判断工具 ========================

/** 去掉末尾 -DUP{n} 消歧后缀 */
export function stripDupSuffix(glCode: string | null | undefined): string {
    if (!glCode) return '';
    return glCode.replace(/-DUP\d+$/, '');
}

/** 是否为 Ex-rate 虚拟列（xxx-RATE / xxx-RATE-DUP{n}） */
export function isRateGlCode(glCode: string | null | undefined): boolean {
    if (!glCode) return false;
    return stripDupSuffix(glCode).endsWith('-RATE');
}

/** 是否为 Ex HKD 折算列专用后缀（xxx-EXHKD / xxx-EXHKD-DUP{n}） */
export function isExHkdGlCode(glCode: string | null | undefined): boolean {
    if (!glCode) return false;
    return stripDupSuffix(glCode).endsWith('-EXHKD');
}

/** Excel 折算列：Ex HKD 或 “… to HKD”（Wealth Gate: Bank SA NZD to HKD） */
export function isExHkdColumnName(nameEn: string | null | undefined): boolean {
    const n = (nameEn || '').toLowerCase();
    return n.includes('ex hkd') || n.includes('to hkd');
}

/** 从 RATE glCode 还原原币 base：A-CA-HCH-RATE-DUP38 → A-CA-HCH */
export function rateOriginalBaseGlCode(rateGlCode: string | null | undefined): string | null {
    if (!isRateGlCode(rateGlCode)) return null;
    const base = stripDupSuffix(rateGlCode!);
    return base.slice(0, -'-RATE'.length);
}

export function isSameGlCodeOrDup(glCode: string, baseGlCode: string): boolean {
    if (!glCode || !baseGlCode) return false;
    if (glCode === baseGlCode) return true;
    return new RegExp(`^${baseGlCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-DUP\\d+$`).test(glCode);
}

/** 是否为 BS-CA-CASH 下的银行/现金列 */
export function isBankCashColumn(def: GlColumnDef): boolean {
    return def.fsCode === 'BS-CA-CASH';
}

/** 是否为 Ex-rate 列（不参与金额汇总） */
export function isExRateColumn(def: GlColumnDef): boolean {
    const name = (def.nameEn || '').trim().toLowerCase();
    return isRateGlCode(def.glCode)
        || name === 'ex-rate'
        || name === 'ex-'
        || name.startsWith('ex-rate');
}

// ======================== Original Mode: S/A + C/A 合并 ========================

export interface BankSaCaResult {
    /** S/A 合并列使用的 columnKey 列表（取 HKD 等价值列：HKD 直接取，外币取 Ex HKD 列） */
    saKeys: string[];
    /** C/A 合并列使用的 columnKey 列表 */
    caKeys: string[];
    /** Cash on hand 独立列定义（可能为 null） */
    cashOnHandDef: GlColumnDef | null;
    /** 合并列最小 columnIndex（用于排序） */
    minColIndex: number;
}

/**
 * 从 bank/cash 列定义中分类 S/A、C/A、Cash on hand
 *
 * 规则（基于 nameEn）：
 * - 含 'S/A' → S/A 组。若为外币(Ex HKD)列 → 取该列 key；若为 HKD 直接列 → 取该列 key
 * - 含 'C/A' → C/A 组
 * - 'Cash on hand' → 独立
 * - 'Ex-rate' / 含 'Ex-rate' → 跳过（不参与合并求和）
 * - 含 'Ex HKD' → 外币折算列，取此列（HKD 等价值）
 *
 * 注意：对于外币银行列（如 HSBC USD S/A），原币列**不参与**合并求和，
 *       仅取其对应的 Ex HKD 列（如 HSBC USD S/A Ex HKD）。
 */
export function classifyBankSaCa(defs: GlColumnDef[]): BankSaCaResult {
    const bankDefs = defs.filter(isBankCashColumn);
    const saKeys: string[] = [];
    const caKeys: string[] = [];
    let cashOnHandDef: GlColumnDef | null = null;
    let minColIndex = Infinity;

    // 先找出所有外币原币列的 glCode（这些列有对应的 Ex HKD 列）
    // 外币原币列的特征：后面紧跟 Ex-rate 和 Ex HKD 列
    const fxOriginalKeys = new Set<string>();
    const sorted = [...bankDefs].sort((a, b) => a.columnIndex - b.columnIndex);
    for (let i = 0; i < sorted.length - 2; i++) {
        const col1 = sorted[i];
        const col2 = sorted[i + 1];
        const col3 = sorted[i + 2];
        if (col2.columnIndex !== col1.columnIndex + 1 || col3.columnIndex !== col1.columnIndex + 2) continue;
        if (isExRateColumn(col2) && col3.nameEn?.includes('Ex HKD')) {
            fxOriginalKeys.add(buildColumnKey(col1));
        }
    }

    for (const d of sorted) {
        if (d.columnIndex < minColIndex) minColIndex = d.columnIndex;
        const name = d.nameEn || '';
        const nameLower = name.toLowerCase();

        // 跳过 Ex-rate 列
        if (isExRateColumn(d)) continue;

        // 跳过外币原币列（它们的 HKD 等价值由 Ex HKD 列提供）
        if (fxOriginalKeys.has(buildColumnKey(d))) continue;

        // Cash on hand
        if (nameLower.includes('cash on hand')) {
            cashOnHandDef = d;
            continue;
        }

        const colKey = buildColumnKey(d);
        if (nameLower.includes('s/a')) {
            saKeys.push(colKey);
        } else if (nameLower.includes('c/a')) {
            caKeys.push(colKey);
        }
    }

    return { saKeys, caKeys, cashOnHandDef, minColIndex };
}

/** 对一组 columnKey 的值求和 */
export function sumByKeys(values: Record<string, number>, keys: string[]): number {
    let total = 0;
    for (const k of keys) {
        total += values[k] ?? 0;
    }
    return total;
}

// ======================== Tiny Mode: 银行分组 ========================

export interface BankGroup {
    /** 组标签，如 "HSBC HKD", "HSBC USD", "CCB HKD", "Cash" */
    groupLabel: string;
    /** 该组包含的列定义（按 columnIndex 有序） */
    columns: GlColumnDef[];
    /** 该组的 columnKey 列表 */
    columnKeys: string[];
    /** true = 仅 HKD 列（S/A+C/A），false = 含外币+汇率+折算 */
    isHkdGroup: boolean;
}

/**
 * 按银行+币种分组（Tiny Mode 用）
 *
 * 分组规则（基于 nameEn 前缀）：
 * - "HSBC HKD S/A"        → group = "HSBC HKD"
 * - "HSBC HKD C/A"        → group = "HSBC HKD"
 * - "HSBC USD S/A"        → group = "HSBC USD"
 * - "Ex-rate"             → 跟随前一列的 group
 * - "HSBC USD S/A Ex HKD" → group = "HSBC USD"
 * - "Cash on hand"        → group = "Cash"
 */
export function buildBankGroups(defs: GlColumnDef[]): BankGroup[] {
    const bankDefs = defs.filter(isBankCashColumn).sort((a, b) => a.columnIndex - b.columnIndex);
    if (bankDefs.length === 0) return [];

    const groupMap = new Map<string, { columns: GlColumnDef[]; columnKeys: string[] }>();
    let lastGroupLabel = '';

    for (const d of bankDefs) {
        const name = d.nameEn || '';
        const nameLower = name.toLowerCase();
        let groupLabel: string;

        if (nameLower.includes('cash on hand')) {
            groupLabel = 'Cash';
        } else if (isExRateColumn(d)) {
            // Ex-rate 跟随前一列的分组
            groupLabel = lastGroupLabel || 'Unknown';
        } else if (isExHkdColumnName(nameLower)) {
            // "HSBC USD S/A Ex HKD" → 取前缀到币种
            groupLabel = extractBankCurrencyPrefix(name.replace(/\s*(Ex\s*HKD|to\s*HKD).*/i, ''));
        } else {
            // "HSBC HKD S/A" → 取前缀到币种
            groupLabel = extractBankCurrencyPrefix(name);
        }

        lastGroupLabel = groupLabel;

        let entry = groupMap.get(groupLabel);
        if (!entry) {
            entry = { columns: [], columnKeys: [] };
            groupMap.set(groupLabel, entry);
        }
        entry.columns.push(d);
        entry.columnKeys.push(buildColumnKey(d));
    }

    const result: BankGroup[] = [];
    for (const [groupLabel, entry] of groupMap) {
        // 外币组：含 Ex-rate 或 Ex HKD（API 常剔除 -RATE 列，不能只靠 Ex-rate 判断）
        const isHkdGroup = !entry.columns.some((c) =>
            isExRateColumn(c) || isExHkdColumnName(c.nameEn),
        );
        result.push({ groupLabel, columns: entry.columns, columnKeys: entry.columnKeys, isHkdGroup });
    }
    return result;
}

/** 账户类型后缀，用于 Tiny Mode 拆分 S/A 与 C/A */
export type BankAccountSubType = 'sa' | 'ca' | 'cash' | 'fx';

/**
 * 汇总时应排除的银行列 key（外币原币 + Ex-rate，仅 Ex HKD / HKD 直账户计入合计）。
 */
export function buildExcludedBankSummaryKeys(defs: GlColumnDef[]): Set<string> {
    const excluded = new Set<string>();
    const bankDefs = defs.filter(isBankCashColumn).sort((a, b) => a.columnIndex - b.columnIndex);
    const bankKeys = new Set(bankDefs.map((d) => buildColumnKey(d)));

    for (const d of bankDefs) {
        const key = buildColumnKey(d);
        if (isExRateColumn(d) || isRateGlCode(d.glCode)) {
            excluded.add(key);
            continue;
        }
        const curr = (d.currency || '').toUpperCase();
        if (curr === 'RATE' || (curr && curr !== 'HKD')) {
            excluded.add(key);
        }
    }

    const exHkdKeys = new Set<string>();
    for (const d of bankDefs) {
        const key = buildColumnKey(d);
        if (isExHkdColumnName(d.nameEn) || isExHkdGlCode(d.glCode)) {
            exHkdKeys.add(key);
        }
    }

    for (let i = 0; i < bankDefs.length - 2; i++) {
        const c1 = bankDefs[i];
        const c2 = bankDefs[i + 1];
        const c3 = bankDefs[i + 2];
        if (c2.columnIndex !== c1.columnIndex + 1 || c3.columnIndex !== c1.columnIndex + 2) continue;
        if (isExRateColumn(c2) && isExHkdColumnName(c3.nameEn)) {
            excluded.add(buildColumnKey(c1));
            excluded.add(buildColumnKey(c2));
            // c3 Ex HKD 计入合计，不排除
        }
    }

    // RATE 列存在时，仅排除精确原币 base（勿排除 base-DUPn：Ex HKD 回退时常为 BS-CA-CASH-DUPn）
    for (const key of bankKeys) {
        const glCode = key.includes('|') ? key.slice(key.indexOf('|') + 1) : key;
        const originalBase = rateOriginalBaseGlCode(glCode);
        if (!originalBase) continue;
        excluded.add(key);
        const exactOriginalKey = `BS-CA-CASH|${originalBase}`;
        if (
            bankKeys.has(exactOriginalKey)
            && !exHkdKeys.has(exactOriginalKey)
            && !isExHkdGlCode(originalBase)
        ) {
            excluded.add(exactOriginalKey);
        }
    }
    for (const k of exHkdKeys) excluded.delete(k);
    return excluded;
}

/** 仅凭 summary key 排除银行辅助列（defs/currency 丢失时的兜底） */
export function buildExcludedBankSummaryKeysFromDataKeys(dataKeys: Iterable<string>): Set<string> {
    const allKeys = new Set(Array.from(dataKeys).filter((k) => k.startsWith('BS-CA-CASH|')));
    const excluded = new Set<string>();
    const exHkdKeys = new Set<string>();
    for (const key of allKeys) {
        const glCode = key.slice(key.indexOf('|') + 1);
        if (isExHkdGlCode(glCode)) exHkdKeys.add(key);
        if (isRateGlCode(glCode)) {
            excluded.add(key);
        }
    }
    for (const key of allKeys) {
        const glCode = key.slice(key.indexOf('|') + 1);
        const originalBase = rateOriginalBaseGlCode(glCode);
        if (!originalBase) continue;
        excluded.add(key);
        const exactOriginalKey = `BS-CA-CASH|${originalBase}`;
        if (
            allKeys.has(exactOriginalKey)
            && !exHkdKeys.has(exactOriginalKey)
            && !isExHkdGlCode(originalBase)
        ) {
            excluded.add(exactOriginalKey);
        }
    }
    for (const k of exHkdKeys) excluded.delete(k);
    return excluded;
}

/** 从列名提取账户子类型 */
export function getBankAccountSubType(nameEn: string): BankAccountSubType | null {
    const n = (nameEn || '').toLowerCase();
    if (n.includes('cash on hand')) return 'cash';
    if (isExHkdColumnName(n) || n.includes('ex-rate')) return 'fx';
    if (n.includes('s/a')) return 'sa';
    if (n.includes('c/a')) return 'ca';
    return null;
}

/** 列标题去掉银行+币种前缀后的后缀，如 "HSBC HKD S/A" → "S/A" */
export function bankColumnShortLabel(nameEn: string, groupLabel: string): string {
    const trimmed = nameEn.replace(groupLabel, '').trim();
    if (trimmed) return trimmed;
    if (isExRateColumn({ nameEn, glCode: '' } as GlColumnDef)) return 'Ex-rate';
    return nameEn;
}

/**
 * 将银行组按 S/A、C/A、Cash、外币 拆分为独立展示组（Tiny Mode 用）。
 * HKD 直账户：S/A 与 C/A 分开；外币组保持 S/A + Ex-rate + Ex HKD 三列一体。
 */
export function splitBankGroupsByAccountType(groups: BankGroup[]): BankGroup[] {
    const result: BankGroup[] = [];

    for (const group of groups) {
        if (!group.isHkdGroup) {
            result.push(group);
            continue;
        }

        const saCols = group.columns.filter(c => getBankAccountSubType(c.nameEn) === 'sa');
        const caCols = group.columns.filter(c => getBankAccountSubType(c.nameEn) === 'ca');
        const cashCols = group.columns.filter(c => getBankAccountSubType(c.nameEn) === 'cash');
        const otherCols = group.columns.filter(c => {
            const t = getBankAccountSubType(c.nameEn);
            return t !== 'sa' && t !== 'ca' && t !== 'cash';
        });

        const pushSubGroup = (label: string, cols: GlColumnDef[]) => {
            if (cols.length === 0) return;
            result.push({
                groupLabel: label,
                columns: cols,
                columnKeys: cols.map(c => buildColumnKey(c)),
                isHkdGroup: true,
            });
        };

        pushSubGroup(`${group.groupLabel} S/A`, saCols);
        pushSubGroup(`${group.groupLabel} C/A`, caCols);
        pushSubGroup(`${group.groupLabel} Cash`, cashCols);
        if (otherCols.length > 0) {
            pushSubGroup(group.groupLabel, otherCols);
        }
    }

    return result;
}

/** 过滤组内全空列（所有行与汇总均为 0） */
export function filterNonEmptyBankColumns(
    group: BankGroup,
    rows: GlSheetRowVO[],
    summary: Record<string, number>,
): GlColumnDef[] {
    return group.columns.filter(col => {
        if (isExRateColumn(col)) return false;
        const key = buildColumnKey(col);
        if ((summary[key] ?? 0) !== 0) return true;
        return rows.some(row => {
            const v = row.columnValues?.[key];
            return v != null && v !== 0;
        });
    });
}

/**
 * 从 nameEn 中提取银行+币种前缀
 * "HSBC HKD S/A"  → "HSBC HKD"
 * "CCB USD S/A"   → "CCB USD"
 * "HSBC HKD C/A"  → "HSBC HKD"
 * 取 nameEn 去掉 S/A、C/A、Ex HKD 等后缀后的前缀
 */
function extractBankCurrencyPrefix(nameEn: string): string {
    // 去掉 S/A, C/A 等后缀
    const cleaned = nameEn
        .replace(/\s+/g, ' ')
        .replace(/\s+(S\/A|C\/A|Ex\s+HKD|to\s+HKD).*$/i, '')
        .trim();
    return cleaned || nameEn.replace(/\s+/g, ' ').trim();
}

function normalizeBankName(name: string | null | undefined): string {
    return (name || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

// ======================== 外币三列：原币 / Ex-rate / Ex HKD ========================

export interface FxBankTrio {
    originalKey: string;
    rateKey: string;
    hkdKey: string;
    /** 原币币种，如 USD */
    currency: string;
}

const FX_CURRENCY_RE = /\b(USD|CNY|EUR|GBP|JPY|AUD|SGD|THB|CAD|CHF|NZD)\b/i;

export function resolveFxCurrency(def: GlColumnDef): string {
    const c = (def.currency || '').trim().toUpperCase();
    if (c && c !== 'HKD' && c !== 'RATE') return c;
    const m = (def.nameEn || '').match(FX_CURRENCY_RE);
    return m ? m[1].toUpperCase() : 'FX';
}

/** 银行外币原币列（不是 Ex-rate、不是 Ex HKD） */
export function isFxOriginalColumn(def: Pick<GlColumnDef, 'fsCode' | 'glCode' | 'nameEn' | 'currency'> | null | undefined): boolean {
    if (!def || def.fsCode !== 'BS-CA-CASH') return false;
    if (isRateGlCode(def.glCode) || isExHkdGlCode(def.glCode)) return false;
    const name = (def.nameEn || '').toLowerCase();
    if (isExHkdColumnName(name) || name === 'ex-rate' || name.startsWith('ex-rate')) return false;
    const currency = (def.currency || '').trim().toUpperCase();
    if (currency && currency !== 'HKD' && currency !== 'RATE') return true;
    return FX_CURRENCY_RE.test(def.nameEn || '');
}

/**
 * 原币列对应的港币账户列。把名称里的外币换成 HKD 后精确匹配，
 * 例如 "HSBC USD S/A" → "HSBC HKD S/A"。
 */
export function hkdCounterpartColumnKey(def: GlColumnDef, defs: GlColumnDef[]): string | null {
    if (!isFxOriginalColumn(def)) return null;
    const name = (def.nameEn || '').replace(/\s+/g, ' ').trim();
    if (!FX_CURRENCY_RE.test(name)) return null;
    const want = name.replace(FX_CURRENCY_RE, 'HKD').replace(/\s+/g, ' ').trim().toLowerCase();
    const self = buildColumnKey(def);
    const match = defs.find((other) => {
        if (buildColumnKey(other) === self) return false;
        return (other.nameEn || '').replace(/\s+/g, ' ').trim().toLowerCase() === want;
    });
    return match ? buildColumnKey(match) : null;
}

/** 原币列对应的 Ex-rate column key：BS-CA-CASH|A-CA-HUH-RATE */
export function fxRateColumnKey(def: Pick<GlColumnDef, 'fsCode' | 'glCode'>): string {
    return `${def.fsCode}|${stripDupSuffix(def.glCode)}-RATE`;
}

/** 从 *-RATE key 还原原币 column key（去掉 -RATE 与 -DUP） */
export function originalKeyFromRateKey(rateKey: string): string | null {
    const sep = rateKey.indexOf('|');
    const fs = sep >= 0 ? rateKey.slice(0, sep) : '';
    const gl = sep >= 0 ? rateKey.slice(sep + 1) : rateKey;
    const base = rateOriginalBaseGlCode(gl);
    if (!fs || !base) return null;
    return `${fs}|${base}`;
}

/**
 * 从列定义中识别外币三元组。
 * 优先 [原币, Ex-rate, Ex HKD]；API 常剔除 -RATE 列时退化为 [原币, Ex HKD]，
 * rateKey 仍按 `{originalGl}-RATE` 推断，供 row.columnValues 取值或 HKD/原币推算。
 */
export function resolveFxTrios(defs: GlColumnDef[]): FxBankTrio[] {
    const sorted = defs.filter(isBankCashColumn).sort((a, b) => a.columnIndex - b.columnIndex);
    const trios: FxBankTrio[] = [];
    const usedHkd = new Set<string>();

    for (let i = 0; i < sorted.length - 2; i++) {
        const c1 = sorted[i];
        const c2 = sorted[i + 1];
        const c3 = sorted[i + 2];
        if (c2.columnIndex !== c1.columnIndex + 1 || c3.columnIndex !== c1.columnIndex + 2) continue;
        if (!isExRateColumn(c2)) continue;
        if (!isExHkdColumnName(c3.nameEn)) continue;
        const hkdKey = buildColumnKey(c3);
        trios.push({
            originalKey: buildColumnKey(c1),
            rateKey: buildColumnKey(c2),
            hkdKey,
            currency: resolveFxCurrency(c1),
        });
        usedHkd.add(hkdKey);
    }

    for (const hkdCol of sorted) {
        if (!isExHkdColumnName(hkdCol.nameEn)) continue;
        const hkdKey = buildColumnKey(hkdCol);
        if (usedHkd.has(hkdKey)) continue;

        const baseName = (hkdCol.nameEn || '').replace(/\s*(Ex\s*HKD|to\s*HKD).*$/i, '').trim();
        const baseNorm = normalizeBankName(baseName);
        const prefix = extractBankCurrencyPrefix(baseName);

        let original = sorted.find((c) => {
            if (c === hkdCol || isExRateColumn(c)) return false;
            if (isExHkdColumnName(c.nameEn)) return false;
            const n = normalizeBankName(c.nameEn);
            return n === baseNorm || n.startsWith(`${baseNorm} `) || n.startsWith(`${baseNorm}#`);
        });
        if (!original) {
            original = sorted
                .filter((c) => {
                    if (c === hkdCol || isExRateColumn(c)) return false;
                    if (isExHkdColumnName(c.nameEn)) return false;
                    if (c.columnIndex >= hkdCol.columnIndex) return false;
                    return extractBankCurrencyPrefix(c.nameEn || '') === prefix;
                })
                .sort((a, b) => b.columnIndex - a.columnIndex)[0];
        }
        if (!original) continue;

        const origGl = stripDupSuffix(original.glCode);
        trios.push({
            originalKey: buildColumnKey(original),
            rateKey: `${original.fsCode}|${origGl}-RATE`,
            hkdKey,
            currency: resolveFxCurrency(original),
        });
        usedHkd.add(hkdKey);
    }

    return trios;
}

function formatFxNumber(n: number, maxDp = 2): string {
    return Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: maxDp,
    });
}

export function resolveRateValue(values: Record<string, number>, trio: FxBankTrio, original: number): number {
    let rate = Number(values[trio.rateKey]) || 0;
    if (rate !== 0) return Math.abs(rate);

    const sep = trio.originalKey.indexOf('|');
    if (sep >= 0) {
        const fs = trio.originalKey.slice(0, sep);
        const baseGl = stripDupSuffix(trio.originalKey.slice(sep + 1));
        for (const [k, raw] of Object.entries(values)) {
            if (!k.startsWith(`${fs}|`)) continue;
            const gl = k.slice(k.indexOf('|') + 1);
            if (rateOriginalBaseGlCode(gl) === baseGl) {
                const v = Number(raw) || 0;
                if (v !== 0) return Math.abs(v);
            }
        }
    }

    const hkd = Number(values[trio.hkdKey]) || 0;
    if (hkd !== 0 && original !== 0) return Math.abs(hkd / original);
    return 0;
}

/**
 * 格式化为 "219.12 USD * 7.84"。无原币时返回 null。
 * 缺汇率时用 |Ex HKD| / |原币| 推算。
 */
export function formatFxConversion(
    values: Record<string, number> | undefined,
    trio: FxBankTrio,
): string | null {
    if (!values) return null;
    const original = Number(values[trio.originalKey]) || 0;
    if (original === 0) return null;

    const rate = resolveRateValue(values, trio, original);
    if (rate === 0) {
        return `${formatFxNumber(original)} ${trio.currency}`;
    }
    return `${formatFxNumber(original)} ${trio.currency} * ${formatFxNumber(rate, 4)}`;
}

/** 多组 trio 时拼接；仅展示本账户相关（hkdKey 落在 accountKeys 内）的转换 */
export function formatFxConversionForAccount(
    values: Record<string, number> | undefined,
    trios: FxBankTrio[] | undefined,
    accountKeys?: string[],
): string | null {
    if (!trios?.length) return null;
    const keySet = accountKeys?.length ? new Set(accountKeys) : null;
    const parts: string[] = [];
    for (const trio of trios) {
        if (keySet && !keySet.has(trio.hkdKey) && !keySet.has(trio.originalKey)) continue;
        const text = formatFxConversion(values, trio);
        if (text) parts.push(text);
    }
    return parts.length > 0 ? parts.join('; ') : null;
}
